// Public college pages (#2579, batch 1 #2596): official facts only, one data
// file per school, each figure tied to a source in its SOURCES map with the
// date it was read. A school is listed here only after every figure on its
// page was checked against that source. Fit scores, odds, profile-derived
// data and the self-reported decision records never go in these files.
//
// The shape (pages/CollegePage.jsx renders it; CollegePages.test.jsx checks it):
//   slug, kbId, name, shortName, location, control ('Public' | 'Private'),
//   aliases? [nicknames the /colleges search should find, e.g. 'Cal'; initials
//     like UCSD are derived from the name, #2650],
//   lastChecked 'YYYY-MM-DD', sources { key: { label, url, read } },
//   branding { primary, secondary, logo_url } (the KB's sourced colours, or null)
//   admission { entering, cdsCycle (only when the source is a dated CDS, else
//     null), applied, admitted, enrolled?, source, where?,
//     residency? [{ group, applied, admitted }], residencySource?, residencyWhere? }
//   byMajor { published, note, year (with rows), rows? [{ name, rate, applied?, admitted? }], head?, source, where? }
//   earlyRounds? { title?, note, year (with rows), rows? [{ plan, applied, admitted }], source, where? }
//   programs? { entering, intro, rows [{ school, requirement, source? }], note?, source }
//   deadlines { entering, headline { label, value }, rows [{ plan, deadline,
//     decision, binding }], note?, source, binding?, bindingSource? }
//   testing { entering, policy, blind, line, source, cdsLine?, cdsWhere? }
//   currentChanges? [{ topic, line, source }] (announced changes already in
//     effect for this cycle's applicants)
//   nextCycle { entering 'Fall 2028', checked 'YYYY-MM-DD', changes [{ topic,
//     line, source }] (empty = nothing announced yet), source }
//   cost { year, columns? ['In-state', 'Out-of-state'], rows [{ item, amount }
//     or { item, amounts: [..] }], total | totals, note?, source, where?,
//     netPrice { average, byIncome [{ income, amount }], note, tileDetail, source } }
//   faq? { answer { text, source, where? } (one sentence, shown under the name), questions
//     [{ q, a, source, where? }] (3 to 5; the page's FAQ and its FAQPage
//     JSON-LD). Every number in it must be one the rest of the page holds
//     (#4090; CollegePages.test.jsx).
// Every data file in this folder is a published page: a file lands here only
// once its facts are checked (a second hand-kept list would drift).
const files = import.meta.glob('./*.js', { eager: true });
// Majors (founder 2026-09-30: "majors offered by the college in the public
// page"): majors/<slug>.js, from the school's official catalog. Shape:
//   { slug, read, intro, groups [{ name, source { label, url }, note?,
//     noteSource? (a key in the college's sources), rate? { text, source },
//     majors [{ name, degree | null, note?, flag?, flagSource? }] }] }
const majorFiles = import.meta.glob('./majors/*.js', { eager: true });
const majorsBySlug = Object.fromEntries(Object.values(majorFiles).map((m) => [m.default.slug, m.default]));
// Campus photos (founder 2026-09-30): photos/<slug>.js, written by
// scripts/college-photo.mjs from a Wikimedia Commons file whose license it
// checked (PD, CC0, CC BY, CC BY-SA only). No file, no photo.
const photoFiles = import.meta.glob('./photos/*.js', { eager: true });
const photosBySlug = Object.fromEntries(Object.values(photoFiles).map((m) => [m.default.slug, m.default]));

export const colleges = Object.entries(files)
    .filter(([path]) => path !== './index.js')
    .map(([, mod]) => ({
        ...mod.default,
        majors: majorsBySlug[mod.default.slug] || null,
        photo: photosBySlug[mod.default.slug] || null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

// Cycle labels (#2651, founder 2026-09-30): every requirement names the
// cycle it belongs to and every outcome figure its year. A requirement block
// carries its entering term ('Fall 2027'); the applying term is the fall
// before it.
export function cycleOf(entering) {
    const y = Number((/(\d{4})/.exec(String(entering || '')) || [])[1]);
    if (!y) return null;
    return { applying: `fall ${y - 1}`, entering: `fall ${y}`, label: `${y - 1}-${String(y).slice(2)}` };
}
export const cycleLine = (entering) => {
    const c = cycleOf(entering);
    return c ? `For students applying in ${c.applying}, entering ${c.entering}` : null;
};
// College Scorecard net price: the Data Dictionary of the October 15, 2026
// release maps the latest NPT4 fields to academic year 2024-25 (IPEDS
// DCY2025-26, file Merged2025_26); the API's "2025" key is that file. Move
// this only when the Scorecard publishes a newer year, and move every school's
// figures with it in the same PR (#4283: scorecard_verify.py --release, then
// scripts/college_pages/refresh_net_price.py).
export const SCORECARD_NET_PRICE_YEAR = '2024-25';

export const majorCount = (c) => (c.majors ? c.majors.groups.reduce((n, g) => n + g.majors.length, 0) : 0);

// schema.org: the school and its programs, for the page's JSON-LD.
const SITE = 'https://stratiaadmissions.com';

// The photo as schema.org sees it, license and credit included.
export function photoJsonLd(p) {
    return {
        '@type': 'ImageObject',
        contentUrl: `${SITE}${p.src}`,
        caption: p.alt,
        creator: { '@type': 'Person', name: p.author },
        creditText: `${p.author}, ${p.license}, via Wikimedia Commons`,
        ...(p.licenseUrl ? { license: p.licenseUrl } : {}),
        acquireLicensePage: p.sourceUrl,
    };
}

export function collegeJsonLd(c) {
    if (!c.majors && !c.photo) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'CollegeOrUniversity',
        name: c.name,
        ...(c.photo ? { image: photoJsonLd(c.photo) } : {}),
        ...(c.majors ? { hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: `Undergraduate majors at ${c.name}`,
            itemListElement: c.majors.groups.map((g) => ({
                '@type': 'OfferCatalog',
                name: g.name,
                // A program is a Thing, so it sits in the list directly (an
                // Offer's itemOffered does not admit EducationalOccupationalProgram).
                itemListElement: g.majors.map((m) => ({
                    '@type': 'EducationalOccupationalProgram',
                    name: m.name,
                    ...(m.degree ? { educationalCredentialAwarded: m.degree } : {}),
                })),
            })),
        } } : {}),
    };
}

// schema.org FAQPage for the page's own questions (#4090): the same words the
// page shows, so the markup never says what a reader cannot see.
export function collegeFaqJsonLd(c) {
    if (!c.faq || !c.faq.questions || !c.faq.questions.length) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: c.faq.questions.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
        })),
    };
}

export const getCollegeBySlug = (slug) => colleges.find((c) => c.slug === slug) || null;

/** A rate, one decimal, from the counts themselves. */
export const pct = (part, whole) => Math.round((1000 * part) / whole) / 10;

/** First-year acceptance rate, one decimal, from the counts themselves. */
export const acceptanceRate = (c) => pct(c.admission.admitted, c.admission.applied);

/** The testing tile's one line, from the policy the page states exactly. */
export function testingTileDetail(t) {
    if (t.blind) return 'Scores are not considered, even if sent';
    if (/required/i.test(t.policy)) return 'Scores are required';
    return 'Scores are read if you send them';
}

// A negative net price (grants above the listed cost) reads -$2,533, not $-2,533.
export const usd = (n) => `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n)).toLocaleString('en-US')}`;
export const count = (n) => Number(n).toLocaleString('en-US');
